// ── BCH Live — Stream Library ────────────────────────────────────
//
// Embed-based architecture: BCH Nostr is the viewer + chat + tip layer.
// Creators stream from ANY platform (Twitch, YouTube, Kick, Rumble,
// Shosho, etc.). We detect the platform from the URL and embed their
// player — no HLS.js, no video processing, just iframes.
//
// The kind 30311 event carries the stream URL in the `streaming` tag.

export const STREAM_EVENT_KIND = 30311;
export const LIVE_CHAT_KIND = 1311;

// ── Heartbeat / Liveness ─────────────────────────────────────────
export const HEARTBEAT_INTERVAL_MS = 30_000;  // Creator publishes every 30s
// Timeout: how long a stream is still considered "live" without a fresh heartbeat.
// Generous enough to survive relay hiccups, cache staleness, slow re-subscriptions,
// background tab throttling and WebSocket reconnects (8 missed heartbeats), but
// short enough that a creator who simply closes their tab doesn't stay "LIVE"
// in the directory for a quarter of an hour.
export const HEARTBEAT_TIMEOUT_SECS = 240;   // 4 min — ~8 missed heartbeats

// ══════════════════════════════════════════════════════════════════
//  DEBUG LOGGING
//
//  All stream logging goes through `slog` so it can be silenced in one
//  place. Enabled by default; turn it off from the browser console with:
//
//      localStorage.setItem('bchnostr:stream-debug', '0'); location.reload()
//
//  Every line is prefixed [Stream:<area>] so you can filter the console
//  by "[Stream" to see only livestream activity.
// ══════════════════════════════════════════════════════════════════

let _debugEnabled: boolean | null = null;

export function isStreamDebug(): boolean {
  if (_debugEnabled === null) {
    try { _debugEnabled = localStorage.getItem('bchnostr:stream-debug') !== '0'; }
    catch { _debugEnabled = true; }
  }
  return _debugEnabled;
}

/** Timestamped, prefixed console logger for the stream subsystem. */
export function slog(area: string, msg: string, data?: unknown) {
  if (!isStreamDebug()) return;
  const t = new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
  if (data !== undefined) console.log(`[Stream:${area}] ${t} ${msg}`, data);
  else console.log(`[Stream:${area}] ${t} ${msg}`);
}

// NOTE: deliberately uses console.log, NOT console.warn.
// Chrome attaches a full stack trace to every console.warn, and a stream that
// retries a few times per second buries the console in hundreds of lines of
// hls.mjs internals. A "!" marker keeps warnings scannable without the noise.
export function swarn(area: string, msg: string, data?: unknown) {
  if (!isStreamDebug()) return;
  const t = new Date().toISOString().slice(11, 23);
  if (data !== undefined) console.log(`[Stream:${area}] ${t} ! ${msg}`, data);
  else console.log(`[Stream:${area}] ${t} ! ${msg}`);
}

/** Compact one-line summary of a parsed stream, for logging. */
export function describeStream(s: StreamMeta | null): string {
  if (!s) return 'none';
  const age = Math.floor(Date.now() / 1000) - Math.max(
    s.created_at,
    parseInt(s.heartbeat || '0', 10),
    parseInt(s.startedAt || '0', 10),
  );
  return `status=${s.status} isLive=${s.isLive} age=${age}s platform=${s.platformInfo.platform} d=${s.dTag.slice(0, 12)} url=${s.streamingUrl.slice(0, 60)}`;
}

// ── Platform Detection ───────────────────────────────────────────

export type StreamPlatform = 'twitch' | 'youtube' | 'kick' | 'rumble' | 'owncast' | 'hls' | 'unknown';

export interface PlatformInfo {
  platform: StreamPlatform;
  label: string;
  color: string;
  embedUrl: string | null;
  /** Original URL for "open in platform" link */
  originalUrl: string;
}

/** Detect the streaming platform from a URL and return embed info. */
export function detectPlatform(url: string): PlatformInfo {
  if (!url) return { platform: 'unknown', label: 'Unknown', color: '#888', embedUrl: null, originalUrl: url };

  const lower = url.toLowerCase().trim();

  // ── Twitch ──
  // https://www.twitch.tv/username or https://twitch.tv/username
  // Also handles clips/videos but we care about live channels
  const twitchMatch = lower.match(/(?:www\.)?twitch\.tv\/([a-z0-9_]+)/i);
  if (twitchMatch && !['videos', 'directory', 'downloads', 'settings', 'p'].includes(twitchMatch[1])) {
    const channel = twitchMatch[1];
    // Twitch embed requires the parent domain to match where it's embedded.
    // We pass multiple parent params so the embed works on the live domain,
    // localhost during dev, and any preview deployment.
    const host = typeof window !== 'undefined' ? window.location.hostname : 'bchnostr.com';
    // `localhost` covers dev and any WebView wrapper that reports itself as
    // such; Twitch rejects the embed if no parent matches the real ancestor.
    const parents = [...new Set([host, 'bchnostr.com', 'www.bchnostr.com', 'localhost'])];
    const parentParams = parents.map(p => `parent=${p}`).join('&');
    return {
      platform: 'twitch',
      label: 'Twitch',
      color: '#9146FF',
      // autoplay=true + muted=true is required for browsers to allow autoplay
      embedUrl: `https://player.twitch.tv/?channel=${channel}&${parentParams}&autoplay=true&muted=true`,
      originalUrl: url,
    };
  }

  // ── YouTube ──
  // https://www.youtube.com/watch?v=ID
  // https://youtu.be/ID
  // https://www.youtube.com/live/ID
  // https://www.youtube.com/embed/ID
  // IMPORTANT: Match against the ORIGINAL url (not `lower`) because
  // YouTube video IDs are case-sensitive (base64-like).
  let ytId: string | null = null;
  const ytWatchMatch = url.match(/youtube\.com\/watch\?.*v=([A-Za-z0-9_-]+)/i);
  const ytShortMatch = url.match(/youtu\.be\/([A-Za-z0-9_-]+)/i);
  const ytLiveMatch = url.match(/youtube\.com\/live\/([A-Za-z0-9_-]+)/i);
  const ytEmbedMatch = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]+)/i);
  ytId = ytWatchMatch?.[1] || ytShortMatch?.[1] || ytLiveMatch?.[1] || ytEmbedMatch?.[1] || null;

  if (ytId) {
    return {
      platform: 'youtube',
      label: 'YouTube',
      color: '#FF0000',
      embedUrl: `https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1`,
      originalUrl: url,
    };
  }

  // ── Kick ──
  // https://kick.com/username
  const kickMatch = lower.match(/kick\.com\/([a-z0-9_-]+)/i);
  if (kickMatch) {
    const channel = kickMatch[1];
    return {
      platform: 'kick',
      label: 'Kick',
      color: '#53FC18',
      embedUrl: `https://player.kick.com/${channel}`,
      originalUrl: url,
    };
  }

  // ── Rumble ──
  // https://rumble.com/v-XXXXX or https://rumble.com/embed/XXXXX
  if (lower.includes('rumble.com')) {
    const embedMatch = lower.match(/rumble\.com\/embed\/([a-z0-9]+)/i);
    if (embedMatch) {
      return {
        platform: 'rumble',
        label: 'Rumble',
        color: '#85C742',
        embedUrl: `https://rumble.com/embed/${embedMatch[1]}/?pub=4`,
        originalUrl: url,
      };
    }
    // Regular rumble URL — try to extract video ID from the path
    // Rumble URLs are like /v5abcde-title.html — embed ID is the v-code
    const vidMatch = lower.match(/rumble\.com\/(v[a-z0-9]+)/i);
    if (vidMatch) {
      return {
        platform: 'rumble',
        label: 'Rumble',
        color: '#85C742',
        embedUrl: `https://rumble.com/embed/${vidMatch[1]}/?pub=4`,
        originalUrl: url,
      };
    }
    // Can't extract embed ID — link out
    return {
      platform: 'rumble',
      label: 'Rumble',
      color: '#85C742',
      embedUrl: null,
      originalUrl: url,
    };
  }

  // ── HLS fallback (Shosho, Owncast, Livepeer, etc.) ──
  // Only if URL ends in .m3u8 or contains /hls/
  if (lower.includes('.m3u8') || lower.includes('/hls/')) {
    return {
      platform: 'hls',
      label: 'Live Stream',
      color: '#EF4444',
      embedUrl: null, // Handled by HLS player component
      originalUrl: url,
    };
  }

  // ── Unknown — show as external link ──
  swarn('platform', `no embed available for URL — viewer will only get an "Open Stream" link: ${url}`);
  return {
    platform: 'unknown',
    label: 'Live Stream',
    color: '#888',
    embedUrl: null,
    originalUrl: url,
  };
}

// ── Extract username from NIP-05 ─────────────────────────────────
export function extractUsername(nip05: string | undefined | null): string | null {
  if (!nip05 || typeof nip05 !== 'string') return null;
  const lower = nip05.toLowerCase();
  if (!lower.endsWith('@bchnostr.com')) return null;
  const name = lower.split('@')[0];
  if (!name || !/^[a-z0-9_.-]+$/.test(name)) return null;
  return name;
}

// ── Parse stream metadata from a kind 30311 event ────────────────
export interface StreamMeta {
  id: string;
  pubkey: string;
  dTag: string;
  title: string;
  status: 'live' | 'ended' | 'planned' | string;
  streamingUrl: string;
  summary: string;
  image: string | undefined;
  viewers: string | undefined;
  startedAt: string | undefined;
  heartbeat: string | undefined;
  created_at: number;
  isLive: boolean;
  aTag: string;
  platformInfo: PlatformInfo;
}

export function parseStreamEvent(event: any): StreamMeta {
  const tags = event.tags || [];
  const tag = (name: string) => tags.find((t: string[]) => t[0] === name)?.[1];

  const status = tag('status') || 'unknown';
  const streamingUrl = tag('streaming') || '';
  const heartbeat = tag('heartbeat');
  const startedAt = tag('starts') || tag('started_at');
  const dTag = tag('d') || '';

  let isLive = status === 'live';
  if (isLive) {
    const now = Math.floor(Date.now() / 1000);
    const hb = parseInt(heartbeat || '0', 10);
    const st = parseInt(startedAt || '0', 10);
    const mostRecent = Math.max(event.created_at, hb, st);
    const age = now - mostRecent;
    if (age > HEARTBEAT_TIMEOUT_SECS) {
      isLive = false;
      swarn('parse', `stale heartbeat → treating as OFFLINE (age ${age}s > ${HEARTBEAT_TIMEOUT_SECS}s limit)`, {
        pubkey: event.pubkey?.slice(0, 8),
        created_at: event.created_at,
        heartbeat: hb || null,
        starts: st || null,
      });
    }
  }

  const aTag = `${STREAM_EVENT_KIND}:${event.pubkey}:${dTag}`;
  const platformInfo = detectPlatform(streamingUrl);

  return {
    id: event.id,
    pubkey: event.pubkey,
    dTag,
    title: tag('title') || 'BCH Live Stream',
    status,
    streamingUrl,
    summary: tag('summary') || '',
    image: tag('image'),
    viewers: tag('current_participants'),
    startedAt,
    heartbeat,
    created_at: event.created_at,
    isLive,
    aTag,
    platformInfo,
  };
}

// ── Stream info localStorage helpers ─────────────────────────────
export function getStreamInfo(pubkey: string) {
  try {
    return {
      title: localStorage.getItem(`bchnostr:stream-title:${pubkey}`) || '',
      description: localStorage.getItem(`bchnostr:stream-desc:${pubkey}`) || '',
      streamUrl: localStorage.getItem(`bchnostr:stream-url:${pubkey}`) || '',
    };
  } catch {
    return { title: '', description: '', streamUrl: '' };
  }
}

export function setStreamInfo(pubkey: string, info: { title: string; description: string; streamUrl: string }) {
  try {
    localStorage.setItem(`bchnostr:stream-title:${pubkey}`, info.title);
    localStorage.setItem(`bchnostr:stream-desc:${pubkey}`, info.description);
    localStorage.setItem(`bchnostr:stream-url:${pubkey}`, info.streamUrl);
  } catch {}
}

// ── Validate stream URL ──────────────────────────────────────────
export function isValidStreamUrl(url: string): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

// ── Shosho app links (still recommended for mobile) ──────────────
export const SHOSHO_SITE = 'https://shosho.live';
export const SHOSHO_APP_IOS = 'https://apps.apple.com/us/app/shosho-live-stream-on-nostr/id6747323179';
export const SHOSHO_APP_ANDROID = 'https://play.google.com/store/apps/details?id=com.shosho.app';

